import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiPost } from '../../api/apiClient';
import { toastError } from '../common/toast';
import {
  OCCUPANCY_TYPES,
  ROOM_GENDER_OPTIONS,
} from '../../utils/occupancyConstants';

const directions = [
  'North', 'South', 'East', 'West',
  'North-East', 'North-West', 'South-East', 'South-West'
];
const floors = [
  { value: 0, label: 'Ground Floor' },
  { value: 1, label: 'First Floor' },
  { value: 2, label: 'Second Floor' },
  { value: 3, label: 'Third Floor' },
  { value: 4, label: 'Fourth Floor' },
  { value: 5, label: 'Fifth Floor' }
];

const AddRoom = ({ buildingId, buildingOccupancyGender, room, onSuccess, onCancel }) => {
  const isCoLiving = buildingOccupancyGender === OCCUPANCY_TYPES.CO_LIVING;
  const isSingleGenderBuilding =
    buildingOccupancyGender === OCCUPANCY_TYPES.BOYS ||
    buildingOccupancyGender === OCCUPANCY_TYPES.GIRLS;
  const showOccupancyGenderField = isCoLiving || isSingleGenderBuilding;

  const getDefaultOccupancyGender = () => {
    if (room?.occupancy_gender) return room.occupancy_gender;
    if (buildingOccupancyGender === OCCUPANCY_TYPES.BOYS) return OCCUPANCY_TYPES.BOYS;
    if (buildingOccupancyGender === OCCUPANCY_TYPES.GIRLS) return OCCUPANCY_TYPES.GIRLS;
    return '';
  };

  const [form, setForm] = useState({
    beds: room?.beds || '',
    room_type: room?.room_type || '',
    floor: room?.floor >= 0 ? room.floor : '',
    direction: room?.direction || '',
    bed_rent: room?.bed_rent || '',
    occupancy_gender: getDefaultOccupancyGender(),
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isSingleGenderBuilding) {
      setForm((prev) => ({ ...prev, occupancy_gender: buildingOccupancyGender }));
    }
  }, [buildingOccupancyGender, isSingleGenderBuilding]);

  const validate = () => {
    const errs = {};
    if (!room && (!form.beds || Number(form.beds) <= 0)) errs.beds = 'Number of beds is required';
    if (!form.room_type) errs.room_type = 'Room type is required';
    if (!form.bed_rent || Number(form.bed_rent) <= 0) errs.bed_rent = 'Bed Rent is required';
    if (isCoLiving && !form.occupancy_gender) errs.occupancy_gender = 'Occupancy gender is required';
    return errs;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    try {
      if (form.floor === '') {
        form.floor = null;
      }
      if (room !== null) {
        form.beds = room.beds.length;
      }
      const payload = {
        ...form,
        beds: Number(form.beds),
        bed_rent: parseFloat(form.bed_rent),
        building_id: buildingId,
        room_id: room?.room_id,
        occupancy_gender: showOccupancyGenderField ? form.occupancy_gender || null : null,
      };
      const res = await apiPost('/room', payload);
      if (res && res.data && res.data.room_id) {
        onSuccess && onSuccess();
      }
    } catch (e) {
      toastError('Failed to save room details ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rt-details-col" style={{ maxWidth: 500 }}>
      <h4>{room ? 'Edit Room' : 'Add Room'}</h4>
      <div className="rt-details-form-table">
        {!room && (
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">
              Number of Beds <span style={{ color: '#d32f2f' }}>*</span>
            </div>
            <div className="rt-details-form-value">
              <select
                value={form.beds}
                onChange={(e) => handleChange('beds', e.target.value)}
                className={`rt-input rt-dropdown${errors.beds ? ' rt-input-error' : ''}`}
              >
                <option value="">Select</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              {errors.beds && <div className="rt-field-error">{errors.beds}</div>}
            </div>
          </div>
        )}
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">
            Room Type <span style={{ color: '#d32f2f' }}>*</span>
          </div>
          <div className="rt-details-form-value">
            <select
              value={form.room_type}
              onChange={(e) => handleChange('room_type', e.target.value)}
              className={`rt-input rt-dropdown${errors.room_type ? ' rt-input-error' : ''}`}
            >
              <option value="">Select</option>
              <option value="AC">AC</option>
              <option value="Non AC">Non AC</option>
            </select>
            {errors.room_type && <div className="rt-field-error">{errors.room_type}</div>}
          </div>
        </div>
        {showOccupancyGenderField && (
          <div className="rt-details-form-row">
            <div className="rt-details-form-label">
              Occupancy Gender {isCoLiving && <span style={{ color: '#d32f2f' }}>*</span>}
            </div>
            <div className="rt-details-form-value">
              <select
                value={form.occupancy_gender}
                onChange={(e) => handleChange('occupancy_gender', e.target.value)}
                className={`rt-input rt-dropdown${errors.occupancy_gender ? ' rt-input-error' : ''}`}
                disabled={isSingleGenderBuilding}
              >
                <option value="">Select</option>
                {ROOM_GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.occupancy_gender && <div className="rt-field-error">{errors.occupancy_gender}</div>}
            </div>
          </div>
        )}
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">Floor</div>
          <div className="rt-details-form-value">
            <select
              value={form.floor}
              onChange={(e) => handleChange('floor', e.target.value)}
              className="rt-input rt-dropdown"
            >
              <option value="">Select</option>
              {floors.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">Direction</div>
          <div className="rt-details-form-value">
            <select
              value={form.direction}
              onChange={(e) => handleChange('direction', e.target.value)}
              className="rt-input rt-dropdown"
            >
              <option value="">Select</option>
              {directions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="rt-details-form-row">
          <div className="rt-details-form-label">
            Bed Rent <span style={{ color: '#d32f2f' }}>*</span>
          </div>
          <div className="rt-details-form-value">
            <input
              type="number"
              step="0.01"
              value={form.bed_rent}
              onChange={(e) => handleChange('bed_rent', e.target.value)}
              className={`rt-input${errors.bed_rent ? ' rt-input-error' : ''}`}
              placeholder="Enter Bed Rent"
            />
            {errors.bed_rent && <div className="rt-field-error">{errors.bed_rent}</div>}
          </div>
        </div>
        {errors.form && <div className="rt-field-error">{errors.form}</div>}
        <div style={{ textAlign: 'right', marginTop: 16, display: 'flex', gap: 10, justifyContent: 'center' }}>
          <RTButton variant="secondary" onClick={onCancel} disabled={saving}>Cancel</RTButton>
          <RTButton 
            variant="primary" 
            onClick={handleSubmit} 
            disabled={saving}
            style={{ 
              minHeight: '36px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {saving ? (
              <RTSpinner size="small" showMessage={false} />
            ) : (
              room ? 'Update' : 'Save'
            )}
          </RTButton>
        </div>
      </div>
    </div>
  );
};

export default AddRoom;
